import cc from "classcat"
import useTranslations from "hooks/useTranslations"
import useBookerDialog from "components/molecules/BookerDialog"
import { fillWithUtm } from "utils/url"

/**
 * The page's only call to action: the cal.com booker, opened over the page.
 *
 * This is the UK schools trigger — its own pill, its own copy. The dialog it
 * opens lives in `BookerDialog`, shared with the site-wide `RequestButton`.
 * Keeping the visitor here matters most on this page, which takes paid traffic:
 * sending them away mid-pitch means they finish reading the pitch never.
 */

type Props = {
  label: string
  /** `black` on light grounds, `green` on the dark CTA band. */
  bgStyle?: "black" | "green"
  className?: string
}

export default function BookDemoButton({
  label,
  bgStyle = "black",
  className,
}: Props) {
  const { appointmentLink, ukSchoolsBookerTitle, ukSchoolsBookerLead } =
    useTranslations()

  const { onTriggerClick, dialog } = useBookerDialog({
    link: appointmentLink,
    title: ukSchoolsBookerTitle || label,
    lead: ukSchoolsBookerLead,
    embedName: "uk-schools",
  })

  return (
    <>
      <a
        href={fillWithUtm(appointmentLink)}
        onClick={onTriggerClick}
        className={cc([
          "pointer inline-block max-w-md whitespace-pre-wrap rounded-full border-2 border-solid px-6 py-3 text-center font-display text-base font-bold leading-snug no-underline transition duration-150 ease-in-out focus:outline-none focus-visible:ring-4 active:scale-[0.96]",
          {
            "border-zinc-800 bg-zinc-800 text-white focus-visible:ring-zinc-500 active:border-zinc-700 active:bg-zinc-700 lg:py-4":
              bgStyle === "black",
            "border-kkm-green bg-kkm-green text-zinc-900 hover:bg-teal-300 focus-visible:ring-kkm-green/60 lg:py-4":
              bgStyle === "green",
          },
          className,
        ])}
      >
        {label}
      </a>

      {dialog}
    </>
  )
}
